import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import type { Unit } from "../../../../../../store/admin/unitsStore";
import { useDevicesStore, type Device } from "../../../../../../store/admin/devicesStore";
import type { ThemeClasses } from "../../../../../../types/components";

interface DeviceConnectionModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  unit: Unit;
  onClose: () => void;
  onConnect: (deviceId: string) => Promise<void>;
  onDisconnect: (deviceId: string) => Promise<void>;
}

export default function DeviceConnectionModal({
  themeClasses,
  isDarkMode,
  unit,
  onClose,
  onConnect,
  onDisconnect,
}: DeviceConnectionModalProps) {
  const { t } = useTranslation("adminSections");
  const fetchDevicesWithoutVehicle = useDevicesStore((state) => state.fetchDevicesWithoutVehicle);
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const hasConnectedDevice = unit.devices && unit.devices.length > 0;
  const connectedDevice = hasConnectedDevice ? unit.devices[0] : null;

  useEffect(() => {
    if (!hasConnectedDevice) {
      fetchAvailableDevices();
    }
  }, [hasConnectedDevice]);

  const fetchAvailableDevices = async () => {
    setLoadingDevices(true);
    try {
      const devices = await fetchDevicesWithoutVehicle();
      setAvailableDevices(devices);
    } catch (error) {
      console.error("Error fetching available devices:", error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedDevice) return;
    setLoading(true);
    try {
      await onConnect(selectedDevice.id);
      onClose();
    } catch (error) {
      console.error("Error connecting device:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connectedDevice) return;
    setLoading(true);
    try {
      await onDisconnect(connectedDevice.id);
      onClose();
    } catch (error) {
      console.error("Error disconnecting device:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderDisconnectView = () => (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-50"}`}>
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-emerald-500/20 rounded-lg">
            <i className="ri-gps-line text-2xl text-emerald-500"></i>
          </div>
          <div>
            <p className={`font-semibold ${themeClasses.text}`}>
              {connectedDevice?.name || connectedDevice?.identifier || t("units.device.connectedDevice")}
            </p>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {connectedDevice?.model} • {connectedDevice?.identifier}
            </p>
          </div>
        </div>
      </div>

      <p className={`text-center ${themeClasses.textSecondary}`}>
        {t("units.device.disconnectConfirmation")}
      </p>

      <div className="grid grid-cols-2 gap-4 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={`py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("units.form.cancel")}
        </button>
        <button
          type="button"
          onClick={handleDisconnect}
          disabled={loading}
          className={`py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? t("units.device.disconnecting") : t("units.device.disconnect")}
        </button>
      </div>
    </div>
  );

  const renderConnectView = () => (
    <div className="space-y-4">
      {loadingDevices ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : availableDevices.length === 0 ? (
        <div className={`text-center py-8 ${themeClasses.textSecondary}`}>
          <i className="ri-gps-line text-4xl mb-2 block opacity-50"></i>
          <p>{t("units.device.noAvailableDevices")}</p>
        </div>
      ) : (
        <>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.device.selectDevice")}
          </p>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {availableDevices.map((device) => (
              <div
                key={device.id}
                onClick={() => setSelectedDevice(device)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedDevice?.id === device.id
                    ? "border-primary-500 bg-primary-500/10"
                    : `${themeClasses.cardBorder} ${isDarkMode ? "hover:bg-[#3A3A3A]" : "hover:bg-gray-50"}`
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${selectedDevice?.id === device.id ? "bg-primary-500/20" : isDarkMode ? "bg-[#4A4A4A]" : "bg-gray-100"}`}>
                    <i className={`ri-gps-line text-lg ${selectedDevice?.id === device.id ? "text-primary-500" : themeClasses.text}`}></i>
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${themeClasses.text}`}>
                      {device.name || device.identifier}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {device.model} • IMEI: {device.identifier}
                    </p>
                  </div>
                  {selectedDevice?.id === device.id && (
                    <i className="ri-checkbox-circle-fill text-primary-500 text-xl"></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-4 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={`py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("units.form.cancel")}
        </button>
        <button
          type="button"
          onClick={handleConnect}
          disabled={loading || !selectedDevice}
          className={`py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            loading || !selectedDevice ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? t("units.device.connecting") : t("units.device.connect")}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={hasConnectedDevice ? t("units.device.manageDevice") : t("units.device.connectDevice")}
      width="max-w-md"
      content={
        <div>
          <div className={`mb-4 p-3 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-50"}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <i className="ri-truck-line text-lg text-blue-500"></i>
              </div>
              <div>
                <p className={`font-medium text-sm ${themeClasses.text}`}>{unit.name}</p>
                <p className={`text-xs ${themeClasses.textSecondary}`}>{unit.plate}</p>
              </div>
            </div>
          </div>

          {hasConnectedDevice ? renderDisconnectView() : renderConnectView()}
        </div>
      }
    />
  );
}
